import { create } from 'zustand';
import { StorageFolder, SavedSequence, SavedPose, StorageState } from '../types';
import { fetchWithAuth } from '../lib/api';
import { auth } from '../lib/firebase';
import * as Sentry from '@sentry/react';
import { LATEST_TOU_VERSION, LATEST_PRIVACY_VERSION } from '../lib/policyVersions';
import { trackSequenceEvent, trackPoseEvent } from '../lib/analytics';

export const UNSORTED_POSES_ID = 'unsorted-poses';
export const UNSORTED_SEQUENCES_ID = 'unsorted-sequences';
export const SHARED_SEQUENCES_ID = 'shared-sequences';

const UNSORTED_POSE_FOLDER: StorageFolder = { id: UNSORTED_POSES_ID, name: 'Unsorted', parentId: null, category: 'poses', isSystem: true };
const UNSORTED_SEQ_FOLDER: StorageFolder = { id: UNSORTED_SEQUENCES_ID, name: 'Unsorted', parentId: null, category: 'sequences', isSystem: true };
const SHARED_SEQ_FOLDER: StorageFolder = { id: SHARED_SEQUENCES_ID, name: 'Shared', parentId: null, category: 'sequences', isSystem: true };

const ensureUnsortedFolders = (folders: StorageFolder[]): StorageFolder[] => {
  const result = folders.map(f =>
    (f.id === UNSORTED_POSES_ID || f.id === UNSORTED_SEQUENCES_ID || f.id === SHARED_SEQUENCES_ID) && !f.isSystem
      ? { ...f, isSystem: true }
      : f
  );
  if (!result.find(f => f.id === UNSORTED_POSES_ID)) result.push(UNSORTED_POSE_FOLDER);
  if (!result.find(f => f.id === UNSORTED_SEQUENCES_ID)) result.push(UNSORTED_SEQ_FOLDER);
  
  if (auth.currentUser) {
    if (!result.find(f => f.id === SHARED_SEQUENCES_ID)) result.push(SHARED_SEQ_FOLDER);
  } else {
    return result.filter(f => f.id !== SHARED_SEQUENCES_ID);
  }
  
  return result;
};

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 15);
};

const DEFAULT_PREFERENCES = {
  language: 'en',
  compactTimeline: false,
  showJointLabels: false,
  showJointAxes: true,
  mirrorMode: true,
  autoSaveEnabled: false,
  confirmReset: true,
  showAiFeatures: false,
  clothingEnabled: false,
  mannequinGender: 'male' as const
};


const DEFAULT_STATE = {
  folders: [UNSORTED_POSE_FOLDER, UNSORTED_SEQ_FOLDER] as StorageFolder[],
  sequences: [] as SavedSequence[],
  poses: [] as SavedPose[],
  preferences: { ...DEFAULT_PREFERENCES }
};

const showMutationError = () => {
  Promise.all([
    import('./useDanceStore'),
    import('../i18n')
  ]).then(([{ useDanceStore }, { default: i18n }]) => {
    useDanceStore.getState().setToastMessage(
      i18n.t('errors.mutation_failed', 'Change could not be saved. Please try again.'),
      'error'
    );
  }).catch(() => {});
};

// A folder may never contain two sequences with the same name. Copy/Move/
// Paste must always produce a unique name (never block on the collision)
// by appending " (2)", " (3)", ... to the candidate name until it's unique.
const getUniqueSequenceName = (sequences: SavedSequence[], folderId: string | null, candidateName: string, excludeId?: string): string => {
  const namesInFolder = new Set(
    sequences.filter(s => s.folderId === folderId && s.id !== excludeId).map(s => s.name)
  );
  if (!namesInFolder.has(candidateName)) return candidateName;
  let n = 2;
  while (namesInFolder.has(`${candidateName} (${n})`)) n++;
  return `${candidateName} (${n})`;
};

// Same disambiguation rule as getUniqueSequenceName, for poses.
const getUniquePoseName = (poses: SavedPose[], folderId: string | null, candidateName: string, excludeId?: string): string => {
  const namesInFolder = new Set(
    poses.filter(p => p.folderId === folderId && p.id !== excludeId).map(p => p.name)
  );
  if (!namesInFolder.has(candidateName)) return candidateName;
  let n = 2;
  while (namesInFolder.has(`${candidateName} (${n})`)) n++;
  return `${candidateName} (${n})`;
};

// GET /sequences and GET /poses return Firestore doc data verbatim, which
// includes server-injected fields (userId, createdAt, updatedAt) the
// backend's strict Zod schemas don't accept back on POST/PUT. Spreading a
// previously cloud-loaded sequence or pose (duplicateSequence, duplicatePose,
// duplicateFolder, savePose/saveSequence updating an existing one) carries
// those fields along — strip them before any body goes back over the wire.
const SERVER_ONLY_KEYS = ['userId', 'createdAt', 'updatedAt'] as const;
const stripServerOnlyFields = <T extends Record<string, any>>(obj: T): T => {
  const result = { ...obj };
  for (const key of SERVER_ONLY_KEYS) delete (result as any)[key];
  return result;
};

// Sentinel error code thrown by createFolder/renameSequence on a name
// collision. Callers (the create/rename dialogs) catch this and translate
// it via their own useTranslation `t`, so the error renders inline in the
// dialog instead of a toast — letting the user correct the name in place.
export const NAME_COLLISION = 'NAME_COLLISION';

const persistLocalToDisk = (folders: StorageFolder[], poses: SavedPose[], sequences: SavedSequence[]) => {
  if (typeof window === 'undefined') return;
  try {
    const userFolders = folders.filter(f => !f.isSystem);
    localStorage.setItem('choreographer_local_folders', JSON.stringify(userFolders));
    localStorage.setItem('choreographer_local_poses', JSON.stringify(poses));
    localStorage.setItem('choreographer_local_sequences', JSON.stringify(sequences));
  } catch (err) {
    console.error('Failed to save to localStorage:', err);
  }
};

export const getSequenceServerTimestamp = (s: SavedSequence): string | number => {
  return (s as any).updatedAt || s.dateModified || (s as any).createdAt || s.dateCreated;
};

export const checkUnseenUpdates = (sequences: SavedSequence[], lastSeen: Record<string, string | number>, currentUserId?: string): string[] => {
  const unseenIds: string[] = [];
  for (const seq of sequences) {
    const isIncoming = (seq.userId && seq.userId !== currentUserId) ||
                       (!seq.userId && auth.currentUser?.email && seq.collaborators?.includes(auth.currentUser.email));
    if (isIncoming) {
      const serverTs = getSequenceServerTimestamp(seq);
      const lastSeenTs = lastSeen[seq.id];
      if (lastSeenTs === undefined) {
        unseenIds.push(seq.id);
      } else {
        const serverTimeNum = typeof serverTs === 'string' ? Date.parse(serverTs) : Number(serverTs);
        const lastSeenTimeNum = typeof lastSeenTs === 'string' ? Date.parse(lastSeenTs) : Number(lastSeenTs);
        if (!Number.isNaN(serverTimeNum) && !Number.isNaN(lastSeenTimeNum)) {
          if (serverTimeNum > lastSeenTimeNum) {
            unseenIds.push(seq.id);
          }
        } else if (String(serverTs) !== String(lastSeenTs)) {
          unseenIds.push(seq.id);
        }
      }
    }
  }
  return unseenIds;
};

const loadInitialLastSeenServerTimestamps = (): Record<string, string | number> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem('choreographer_shared_seen_server_timestamps');
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.error('Failed to parse choreographer_shared_seen_server_timestamps:', err);
    return {};
  }
};

export const useStorageStore = create<StorageState>()(
  (set, get) => ({
    folders: [UNSORTED_POSE_FOLDER, UNSORTED_SEQ_FOLDER],
    sequences: [],
    poses: [],
    selectedSequenceFolderId: UNSORTED_SEQUENCES_ID,
    selectedPoseFolderId: UNSORTED_POSES_ID,
    lastSeenServerTimestamps: loadInitialLastSeenServerTimestamps(),
    unseenUpdatedSequenceIds: [],
    preferences: { ...DEFAULT_PREFERENCES },

    updatePreference: async (key, value) => {
      set((state) => ({
        preferences: { ...state.preferences, [key]: value }
      }));

      if (auth.currentUser) {
        const newPrefs = get().preferences;
        fetchWithAuth('/api/user/profile', {
          method: 'POST',
          body: JSON.stringify({ preferences: newPrefs })
        }).catch(err => console.error('Failed to sync preference to server:', err));
      }

      if (key === 'language') {
        try {
          const { default: i18n } = await import('../i18n');
          if (i18n.language !== value) {
            await i18n.changeLanguage(value as string);
          }
        } catch (err) {
          console.error('Failed to update language in i18n:', err);
        }
      }
    },

    tutorialProgress: {
      currentModuleId: null,
      currentStepIndex: 0,
      completedModuleIds: []
    },

    anonymousTouVersion: typeof window !== 'undefined' ? localStorage.getItem('choreographer_anonymous_tou_version') : null,
    anonymousPrivacyVersion: typeof window !== 'undefined' ? localStorage.getItem('choreographer_anonymous_privacy_version') : null,
    anonymousTouAcceptedAt: typeof window !== 'undefined' ? localStorage.getItem('choreographer_anonymous_tou_accepted_at') : null,
    anonymousPrivacyAcceptedAt: typeof window !== 'undefined' ? localStorage.getItem('choreographer_anonymous_privacy_accepted_at') : null,
    anonymousTouUpdateRequired: typeof window !== 'undefined'
      ? (() => {
          const v = localStorage.getItem('choreographer_anonymous_tou_version');
          return v !== null && v !== LATEST_TOU_VERSION;
        })()
      : false,
    anonymousPrivacyUpdateRequired: typeof window !== 'undefined'
      ? (() => {
          const v = localStorage.getItem('choreographer_anonymous_privacy_version');
          return v !== null && v !== LATEST_PRIVACY_VERSION;
        })()
      : false,
    setAnonymousConsentVersions: (touVersion, privacyVersion) => set((state) => {
      const touChanged = touVersion !== state.anonymousTouVersion;
      const privacyChanged = privacyVersion !== state.anonymousPrivacyVersion;
      const nextTouAcceptedAt = touVersion
        ? (touChanged ? new Date().toISOString() : state.anonymousTouAcceptedAt)
        : null;
      const nextPrivacyAcceptedAt = privacyVersion
        ? (privacyChanged ? new Date().toISOString() : state.anonymousPrivacyAcceptedAt)
        : null;

      if (typeof window !== 'undefined') {
        if (touVersion) localStorage.setItem('choreographer_anonymous_tou_version', touVersion);
        else localStorage.removeItem('choreographer_anonymous_tou_version');

        if (privacyVersion) localStorage.setItem('choreographer_anonymous_privacy_version', privacyVersion);
        else localStorage.removeItem('choreographer_anonymous_privacy_version');

        if (nextTouAcceptedAt) localStorage.setItem('choreographer_anonymous_tou_accepted_at', nextTouAcceptedAt);
        else localStorage.removeItem('choreographer_anonymous_tou_accepted_at');

        if (nextPrivacyAcceptedAt) localStorage.setItem('choreographer_anonymous_privacy_accepted_at', nextPrivacyAcceptedAt);
        else localStorage.removeItem('choreographer_anonymous_privacy_accepted_at');
      }

      return {
        anonymousTouVersion: touVersion,
        anonymousPrivacyVersion: privacyVersion,
        anonymousTouAcceptedAt: nextTouAcceptedAt,
        anonymousPrivacyAcceptedAt: nextPrivacyAcceptedAt,
        anonymousTouUpdateRequired: touVersion !== LATEST_TOU_VERSION,
        anonymousPrivacyUpdateRequired: privacyVersion !== LATEST_PRIVACY_VERSION,
      };
    }),
    resetAnonymousConsent: () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('choreographer_anonymous_tou_version');
        localStorage.removeItem('choreographer_anonymous_privacy_version');
        localStorage.removeItem('choreographer_anonymous_tou_accepted_at');
        localStorage.removeItem('choreographer_anonymous_privacy_accepted_at');
      }
      set({
        anonymousTouVersion: null,
        anonymousPrivacyVersion: null,
        anonymousTouAcceptedAt: null,
        anonymousPrivacyAcceptedAt: null,
        anonymousTouUpdateRequired: false,
        anonymousPrivacyUpdateRequired: false,
      });
    },
    makeConsentStale: () => {
      const staleDate = '2020-01-01T00:00:00.000Z';
      if (typeof window !== 'undefined') {
        localStorage.setItem('choreographer_anonymous_tou_version', 'stale');
        localStorage.setItem('choreographer_anonymous_privacy_version', 'stale');
        localStorage.setItem('choreographer_anonymous_tou_accepted_at', staleDate);
        localStorage.setItem('choreographer_anonymous_privacy_accepted_at', staleDate);
      }
      set({
        anonymousTouVersion: 'stale',
        anonymousPrivacyVersion: 'stale',
        anonymousTouAcceptedAt: staleDate,
        anonymousPrivacyAcceptedAt: staleDate,
        anonymousTouUpdateRequired: true,
        anonymousPrivacyUpdateRequired: true,
      });
    },

    updateTutorialProgress: (progress) => set(state => ({
      tutorialProgress: { ...state.tutorialProgress, ...progress }
    })),

    resetTutorial: () => set({
      tutorialProgress: {
        currentModuleId: null,
        currentStepIndex: 0,
        completedModuleIds: []
      }
    }),

    refreshSharedSequences: async () => {
      if (!auth.currentUser) return;
      try {
        const serverSequences = await fetchWithAuth('/api/sequences');
        const currentUserId = auth.currentUser.uid;
        set(state => {
          const updatedMap = new Map<string, SavedSequence>();
          state.sequences.forEach(s => updatedMap.set(s.id, s));
          (serverSequences as SavedSequence[]).forEach(s => updatedMap.set(s.id, s));
          const newSequences = Array.from(updatedMap.values());
          const unseenIds = checkUnseenUpdates(newSequences, state.lastSeenServerTimestamps, currentUserId);
          return {
            sequences: newSequences,
            unseenUpdatedSequenceIds: unseenIds
          };
        });
      } catch (err) {
        console.error('Failed to refresh shared sequences:', err);
      }
    },

    markSequenceAsSeen: (seq: SavedSequence) => {
      const serverTs = getSequenceServerTimestamp(seq);
      set(state => {
        const nextLastSeen = { ...state.lastSeenServerTimestamps, [seq.id]: serverTs };
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('choreographer_shared_seen_server_timestamps', JSON.stringify(nextLastSeen));
          } catch (err) {
            console.error('Failed to persist choreographer_shared_seen_server_timestamps:', err);
          }
        }
        const nextUnseen = state.unseenUpdatedSequenceIds.filter(id => id !== seq.id);
        return {
          lastSeenServerTimestamps: nextLastSeen,
          unseenUpdatedSequenceIds: nextUnseen
        };
      });
    },

    setSelectedSequenceFolderId: (id) => {
      set({ selectedSequenceFolderId: id });
      if (id === SHARED_SEQUENCES_ID) {
        get().refreshSharedSequences();
      }
    },
    setSelectedPoseFolderId: (id) => set({ selectedPoseFolderId: id }),

    loadFromServer: async () => {
      try {
        const [serverFolders, poses, sequences] = await Promise.all([
          fetchWithAuth('/api/folders'),
          fetchWithAuth('/api/poses'),
          fetchWithAuth('/api/sequences')
        ]);
        const folders = ensureUnsortedFolders(serverFolders);
        for (const folder of [UNSORTED_POSE_FOLDER, UNSORTED_SEQ_FOLDER]) {
          if (!serverFolders.find((f: StorageFolder) => f.id === folder.id)) {
            fetchWithAuth('/api/folders', {
              method: 'POST',
              body: JSON.stringify({ id: folder.id, name: folder.name, parentId: null, category: folder.category, isSystem: true })
            }).catch(err => console.error('Failed to sync Unsorted folder creation', err));
          }
        }
        // Backfill dateCreated/dateModified for sequences saved before these
        // fields existed, so older cloud data doesn't render an invalid date.
        const sequencesNeedingBackfill: SavedSequence[] = [];
        const migratedSequences = (sequences as SavedSequence[]).map(s => {
          if (s.dateCreated != null && s.dateModified != null) return s;
          const serverCreated = (s as any).createdAt ? Date.parse((s as any).createdAt) : NaN;
          const serverModified = (s as any).updatedAt ? Date.parse((s as any).updatedAt) : NaN;
          const migrated = {
            ...s,
            dateCreated: s.dateCreated ?? (Number.isFinite(serverCreated) ? serverCreated : Date.now()),
            dateModified: s.dateModified ?? (Number.isFinite(serverModified) ? serverModified : (Number.isFinite(serverCreated) ? serverCreated : Date.now())),
          };
          sequencesNeedingBackfill.push(migrated);
          return migrated;
        });
        for (const seq of sequencesNeedingBackfill) {
          fetchWithAuth(`/api/sequences/${seq.id}`, {
            method: 'PUT',
            body: JSON.stringify({ dateCreated: seq.dateCreated, dateModified: seq.dateModified })
          }).catch(err => console.error('Failed to persist backfilled sequence dates', err));
        }

        const unseenIds = checkUnseenUpdates(migratedSequences, get().lastSeenServerTimestamps, auth.currentUser?.uid);

        set(state => ({
          folders,
          poses,
          sequences: migratedSequences,
          unseenUpdatedSequenceIds: unseenIds,
          selectedPoseFolderId: state.selectedPoseFolderId ?? UNSORTED_POSES_ID,
          selectedSequenceFolderId: state.selectedSequenceFolderId ?? UNSORTED_SEQUENCES_ID,
        }));
      } catch (error) {
        console.error('Failed to load storage from server', error);
        Sentry.captureException(error, {
          level: 'fatal',
          tags: { scenario: 'initial_load_blocked' },
          extra: { userId: auth.currentUser?.uid ?? 'unknown' },
        });
        Promise.all([
          import('./useDanceStore'),
          import('../i18n')
        ]).then(([{ useDanceStore }, { default: i18n }]) => {
          useDanceStore.getState().confirmAction({
            title: i18n.t('errors.load_failed_title', 'Connection Trouble'),
            message: i18n.t('errors.load_failed_message', 'ChoreogrAIphy failed to load the library from the cloud. The backend server is currently unavailable. Please check your connection or reload the studio.'),
            isAlert: true,
            isNonDismissible: true,
            confirmLabel: i18n.t('errors.reload_studio', 'Reload Studio'),
            onConfirm: () => {
              window.location.reload();
            }
          });
        }).catch(e => console.error('Failed to show load error dialog:', e));
      }
    },

    resetToLocal: () => {
      set(DEFAULT_STATE);
    },

    clipboard: null,
    setClipboard: (clipboard) => set({ clipboard }),

    initialize: () => {},

    initializeLocal: () => {
      if (typeof window === 'undefined') return;
      try {
        const localFoldersStr = localStorage.getItem('choreographer_local_folders');
        const localPosesStr = localStorage.getItem('choreographer_local_poses');
        const localSequencesStr = localStorage.getItem('choreographer_local_sequences');

        const localFolders = localFoldersStr ? JSON.parse(localFoldersStr) : [];
        const localPoses = localPosesStr ? JSON.parse(localPosesStr) : [];
        const localSequences = localSequencesStr ? JSON.parse(localSequencesStr) : [];
        // Backfill dateCreated/dateModified for sequences saved before these
        // fields existed, so old local data doesn't render an invalid date.
        const migratedSequences = localSequences.map((s: SavedSequence) => ({
          ...s,
          dateCreated: s.dateCreated ?? Date.now(),
          dateModified: s.dateModified ?? s.dateCreated ?? Date.now(),
        }));

        set({
          folders: ensureUnsortedFolders(localFolders),
          poses: localPoses,
          sequences: migratedSequences,
          selectedPoseFolderId: get().selectedPoseFolderId ?? UNSORTED_POSES_ID,
          selectedSequenceFolderId: get().selectedSequenceFolderId ?? UNSORTED_SEQUENCES_ID,
        });
      } catch (err) {
        console.error('Failed to load local storage data:', err);
        set(DEFAULT_STATE);
      }
    },

    createFolder: (name, category, parentId = null) => {
      const siblingCollision = get().folders.some(f => f.category === category && f.parentId === parentId && f.name === name);
      if (siblingCollision) {
        throw new Error(NAME_COLLISION);
      }

      const id = generateId();
      const parentFolder = parentId ? get().folders.find(f => f.id === parentId) : null;
      const parentName = parentFolder ? parentFolder.name : 'Root';

      if (category === 'sequences') {
        trackSequenceEvent('folder_created', { folder_name: name, parent_folder_name: parentName });
      } else {
        trackPoseEvent('folder_created', { folder_name: name, parent_folder_name: parentName });
      }

      set(state => {
        const nextFolders = [...state.folders, { id, name, parentId, category }];
        if (!auth.currentUser) {
          persistLocalToDisk(nextFolders, state.poses, state.sequences);
        }
        return { folders: nextFolders };
      });

      if (auth.currentUser) {
        fetchWithAuth('/api/folders', {
          method: 'POST',
          body: JSON.stringify({ id, name, parentId, category })
        }).catch(err => {
          console.error('Failed to sync folder creation', err);
          set(state => ({ folders: state.folders.filter(f => f.id !== id) }));
          showMutationError();
        });
      }

      return id;
    },

    renameFolder: (id, newName) => {
      const folder = get().folders.find(f => f.id === id);
      if (folder?.isSystem) return;
      if (folder) {
        if (folder.category === 'sequences') {
          trackSequenceEvent('folder_renamed', { folder_name: newName, previous_name: folder.name });
        } else {
          trackPoseEvent('folder_renamed', { folder_name: newName, previous_name: folder.name });
        }
      }

      const oldName = folder?.name;
      set(state => {
        const nextFolders = state.folders.map(f => f.id === id ? { ...f, name: newName } : f);
        if (!auth.currentUser) {
          persistLocalToDisk(nextFolders, state.poses, state.sequences);
        }
        return { folders: nextFolders };
      });

      if (auth.currentUser) {
        fetchWithAuth(`/api/folders/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: newName })
        }).catch(err => {
          console.error('Failed to sync folder rename', err);
          set(state => ({
            folders: state.folders.map(f => f.id === id ? { ...f, name: oldName! } : f)
          }));
          showMutationError();
        });
      }
    },

    deleteFolder: (id) => {
      const folderToDelete = get().folders.find(f => f.id === id);
      if (folderToDelete?.isSystem) return;
      if (folderToDelete) {
        if (folderToDelete.category === 'sequences') {
          trackSequenceEvent('folder_deleted', { folder_name: folderToDelete.name });
        } else {
          trackPoseEvent('folder_deleted', { folder_name: folderToDelete.name });
        }
      }

      const snapshot = { folders: get().folders, sequences: get().sequences, poses: get().poses };

      set(state => {
        const idsToDelete = new Set<string>([id]);
        let grew = true;
        while (grew) {
          grew = false;
          for (const f of state.folders) {
            if (f.parentId && idsToDelete.has(f.parentId) && !idsToDelete.has(f.id)) {
              idsToDelete.add(f.id);
              grew = true;
            }
          }
        }

        const nextSequences = state.sequences.filter(s => !s.folderId || !idsToDelete.has(s.folderId));
        const nextPoses = state.poses.filter(p => !p.folderId || !idsToDelete.has(p.folderId));
        const nextFolders = state.folders.filter(f => !idsToDelete.has(f.id));
        if (!auth.currentUser) {
          persistLocalToDisk(nextFolders, nextPoses, nextSequences);
        }
        return {
          sequences: nextSequences,
          poses: nextPoses,
          folders: nextFolders
        };
      });

      if (auth.currentUser) {
        fetchWithAuth(`/api/folders/${id}`, { method: 'DELETE' })
          .catch(err => {
            console.error('Failed to sync folder delete', err);
            set(snapshot);
            showMutationError();
          });
      }
    },

    moveFolder: (id, targetParentId) => {
      const folder = get().folders.find(f => f.id === id);
      if (folder?.isSystem) return;
      if (folder) {
        const targetParent = targetParentId ? get().folders.find(f => f.id === targetParentId) : null;
        const targetParentName = targetParent ? targetParent.name : 'Root';
        if (folder.category === 'sequences') {
          trackSequenceEvent('folder_moved', { folder_name: folder.name, target_parent_name: targetParentName });
        } else {
          trackPoseEvent('folder_moved', { folder_name: folder.name, target_parent_name: targetParentName });
        }
      }

      const oldParentId = folder?.parentId ?? null;
      set(state => {
        const nextFolders = state.folders.map(f => f.id === id ? { ...f, parentId: targetParentId } : f);
        if (!auth.currentUser) {
          persistLocalToDisk(nextFolders, state.poses, state.sequences);
        }
        return { folders: nextFolders };
      });

      if (auth.currentUser) {
        fetchWithAuth(`/api/folders/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ parentId: targetParentId })
        }).catch(err => {
          console.error('Failed to sync folder move', err);
          set(state => ({
            folders: state.folders.map(f => f.id === id ? { ...f, parentId: oldParentId } : f)
          }));
          showMutationError();
        });
      }
    },

    duplicateFolder: (id, targetParentId) => {
      type NewFolder = { id: string; name: string; parentId: string | null; category: 'sequences' | 'poses' };
      const newFolders: NewFolder[] = [];
      const newSequences: SavedSequence[] = [];
      const newPoses: SavedPose[] = [];
      let rootNewFolderId = '';

      const buildTree = (folderId: string, parentId: string | null): string => {
        const currFolder = get().folders.find(f => f.id === folderId);
        if (!currFolder) return '';

        const newFolderId = generateId();
        if (!rootNewFolderId) rootNewFolderId = newFolderId;
        const folderName = parentId === targetParentId ? `${currFolder.name} (Copy)` : currFolder.name;
        const parentFolder = parentId ? get().folders.find(f => f.id === parentId) : null;
        const parentName = parentFolder ? parentFolder.name : 'Root';

        if (currFolder.category === 'sequences') {
          trackSequenceEvent('folder_created', { folder_name: folderName, parent_folder_name: parentName, duplicated_from: currFolder.name });
        } else {
          trackPoseEvent('folder_created', { folder_name: folderName, parent_folder_name: parentName, duplicated_from: currFolder.name });
        }

        newFolders.push({ id: newFolderId, name: folderName, parentId, category: currFolder.category });

        get().folders.filter(f => f.parentId === folderId).forEach(child => buildTree(child.id, newFolderId));

        if (currFolder.category === 'sequences') {
          get().sequences.filter(s => s.folderId === folderId).forEach(seq => {
            const newSeqId = generateId();
            trackSequenceEvent('saved', { sequence_name: seq.name, is_new: true, duplicated_from: seq.name });
            newSequences.push({ ...seq, id: newSeqId, folderId: newFolderId, userId: auth.currentUser ? auth.currentUser.uid : undefined });
          });
        } else {
          get().poses.filter(p => p.folderId === folderId).forEach(pose => {
            const newPoseId = generateId();
            trackPoseEvent('saved', { pose_name: pose.name, is_new: true, duplicated_from: pose.name });
            newPoses.push({ ...pose, id: newPoseId, folderId: newFolderId, userId: auth.currentUser ? auth.currentUser.uid : undefined, pose: { ...pose.pose }, cameraState: pose.cameraState ? { ...pose.cameraState } : undefined });
          });
        }

        return newFolderId;
      };

      buildTree(id, targetParentId);

      set(state => {
        const nextFolders = [...state.folders, ...newFolders];
        const nextSequences = [...state.sequences, ...newSequences];
        const nextPoses = [...state.poses, ...newPoses];
        if (!auth.currentUser) {
          persistLocalToDisk(nextFolders, nextPoses, nextSequences);
        }
        return {
          folders: nextFolders,
          sequences: nextSequences,
          poses: nextPoses
        };
      });

      if (auth.currentUser) {
        const apiCalls = [
          ...newFolders.map(f => fetchWithAuth('/api/folders', { method: 'POST', body: JSON.stringify(f) })),
          ...newSequences.map(s => fetchWithAuth('/api/sequences', { method: 'POST', body: JSON.stringify(stripServerOnlyFields(s)) })),
          ...newPoses.map(p => fetchWithAuth('/api/poses', { method: 'POST', body: JSON.stringify(stripServerOnlyFields(p)) })),
        ];

        if (apiCalls.length > 0) {
          const folderIds = new Set(newFolders.map(f => f.id));
          const seqIds = new Set(newSequences.map(s => s.id));
          const poseIds = new Set(newPoses.map(p => p.id));
          Promise.all(apiCalls).catch(err => {
            console.error('Failed to sync folder duplication', err);
            set(state => ({
              folders: state.folders.filter(f => !folderIds.has(f.id)),
              sequences: state.sequences.filter(s => !seqIds.has(s.id)),
              poses: state.poses.filter(p => !poseIds.has(p.id)),
            }));
            showMutationError();
          });
        }
      }

      return rootNewFolderId;
    },

    saveSequence: (sequence) => {
      const folderId = sequence.folderId === SHARED_SEQUENCES_ID ? UNSORTED_SEQUENCES_ID : sequence.folderId;
      const seqWithId = sequence.id ? sequence : { ...sequence, id: generateId() };
      const savedId = (seqWithId as SavedSequence).id;
      const existingSeq = get().sequences.find(s => s.id === savedId);
      const isExisting = !!existingSeq;
      const now = Date.now();
      const dateCreated = isExisting ? existingSeq!.dateCreated : now;
      const userId = sequence.userId ?? (auth.currentUser ? auth.currentUser.uid : undefined);
      const seqWithDates = { ...seqWithId, folderId, dateCreated, dateModified: now, ...(userId ? { userId } : {}) } as SavedSequence;

      trackSequenceEvent('saved', { sequence_name: sequence.name, is_new: !isExisting });

      set(state => {
        const existingIdx = state.sequences.findIndex(s => s.id === savedId);
        let newSequences;
        if (existingIdx >= 0) {
          newSequences = [...state.sequences];
          newSequences[existingIdx] = {
            ...newSequences[existingIdx],
            ...seqWithDates,
          } as SavedSequence;
        } else {
          newSequences = [...state.sequences, seqWithDates];
        }
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, state.poses, newSequences);
        }
        return { sequences: newSequences };
      });

      if (auth.currentUser) {
        fetchWithAuth(isExisting ? `/api/sequences/${savedId}` : '/api/sequences', {
          method: isExisting ? 'PUT' : 'POST',
          body: JSON.stringify(stripServerOnlyFields(seqWithDates))
        }).catch(err => {
          console.error('Failed to sync sequence save', err);
          if (isExisting && existingSeq) {
            set(state => ({ sequences: state.sequences.map(s => s.id === savedId ? existingSeq : s) }));
          } else {
            set(state => ({ sequences: state.sequences.filter(s => s.id !== savedId) }));
          }
          showMutationError();
        });
      }

      return savedId;
    },

    renameSequence: (id, newName) => {
      const seq = get().sequences.find(s => s.id === id);
      if (!seq) return;

      const collision = get().sequences.some(s => s.folderId === seq.folderId && s.id !== id && s.name === newName);
      if (collision) {
        throw new Error(NAME_COLLISION);
      }

      trackSequenceEvent('renamed', { sequence_name: newName, previous_name: seq.name });
      const oldName = seq?.name;

      set(state => {
        const nextSequences = state.sequences.map(s => s.id === id ? { ...s, name: newName } : s);
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, state.poses, nextSequences);
        }
        return { sequences: nextSequences };
      });

      if (auth.currentUser) {
        fetchWithAuth(`/api/sequences/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: newName })
        }).catch(err => {
          console.error('Failed to sync sequence rename', err);
          set(state => ({
            sequences: state.sequences.map(s => s.id === id ? { ...s, name: oldName! } : s)
          }));
          showMutationError();
        });
      }
    },

    duplicateSequence: (id, targetFolderId) => {
      const seq = get().sequences.find(s => s.id === id);
      if (!seq) return '';
      const newId = generateId();
      const folderId = targetFolderId !== undefined ? targetFolderId : seq.folderId;
      const uniqueName = getUniqueSequenceName(get().sequences, folderId, `Copy of ${seq.name}`);
      const duplicated: SavedSequence = {
        ...seq,
        id: newId,
        name: uniqueName,
        folderId,
        userId: auth.currentUser ? auth.currentUser.uid : undefined,
        dateCreated: seq.dateCreated,
        dateModified: seq.dateModified,
      };

      trackSequenceEvent('saved', { sequence_name: duplicated.name, is_new: true, duplicated_from: seq.name });

      set(state => {
        const nextSequences = [...state.sequences, duplicated];
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, state.poses, nextSequences);
        }
        return { sequences: nextSequences };
      });

      if (auth.currentUser) {
        fetchWithAuth('/api/sequences', {
          method: 'POST',
          body: JSON.stringify(stripServerOnlyFields(duplicated))
        }).catch(err => {
          console.error('Failed to sync sequence duplication', err);
          set(state => ({ sequences: state.sequences.filter(s => s.id !== newId) }));
          showMutationError();
        });
      }

      return newId;
    },

    deleteSequence: (id) => {
      const seq = get().sequences.find(s => s.id === id);
      if (seq) {
        trackSequenceEvent('deleted', { sequence_name: seq.name });
      }

      set(state => {
        const nextSequences = state.sequences.filter(s => s.id !== id);
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, state.poses, nextSequences);
        }
        return { sequences: nextSequences };
      });

      if (auth.currentUser) {
        fetchWithAuth(`/api/sequences/${id}`, { method: 'DELETE' })
          .catch(err => {
            console.error('Failed to sync sequence delete', err);
            if (seq) set(state => ({ sequences: [...state.sequences, seq] }));
            showMutationError();
          });
      }
    },

    moveSequence: (id, folderId) => {
      const seq = get().sequences.find(s => s.id === id);
      const folder = get().folders.find(f => f.id === folderId);
      if (seq && folder) {
        trackSequenceEvent('moved', { sequence_name: seq.name, target_folder_name: folder.name });
      }
      const oldFolderId = seq?.folderId ?? null;
      const newName = seq ? getUniqueSequenceName(get().sequences, folderId, seq.name, id) : undefined;

      set(state => {
        const nextSequences = state.sequences.map(s => s.id === id ? { ...s, folderId, ...(newName !== undefined ? { name: newName } : {}) } : s);
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, state.poses, nextSequences);
        }
        return { sequences: nextSequences };
      });

      if (auth.currentUser) {
        fetchWithAuth(`/api/sequences/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ folderId, ...(newName !== undefined ? { name: newName } : {}) })
        }).catch(err => {
          console.error('Failed to sync sequence move', err);
          set(state => ({
            sequences: state.sequences.map(s => s.id === id ? { ...s, folderId: oldFolderId, ...(seq ? { name: seq.name } : {}) } : s)
          }));
          showMutationError();
        });
      }
    },

    multiMoveSequences: (ids, folderId) => {
      const folder = get().folders.find(f => f.id === folderId);
      if (folder) {
        ids.forEach(id => {
          const seq = get().sequences.find(s => s.id === id);
          if (seq) trackSequenceEvent('moved', { sequence_name: seq.name, target_folder_name: folder.name });
        });
      }

      const oldFolderIds = new Map<string, string | null>(ids.map(id => {
        const seq = get().sequences.find(s => s.id === id);
        return [id, seq?.folderId ?? null];
      }));
      const oldNames = new Map<string, string>(ids.map(id => {
        const seq = get().sequences.find(s => s.id === id);
        return [id, seq?.name ?? ''];
      }));

      // Disambiguate sequentially against the running set so two moved
      // sequences sharing a name don't collide with each other either.
      const newNames = new Map<string, string>();
      const runningNames = get().sequences.filter(s => !ids.includes(s.id) && s.folderId === folderId).map(s => s.name);
      ids.forEach(id => {
        const seq = get().sequences.find(s => s.id === id);
        if (!seq) return;
        const taken = new Set(runningNames);
        let uniqueName = seq.name;
        if (taken.has(uniqueName)) {
          let n = 2;
          while (taken.has(`${seq.name} (${n})`)) n++;
          uniqueName = `${seq.name} (${n})`;
        }
        newNames.set(id, uniqueName);
        runningNames.push(uniqueName);
      });

      set(state => {
        const nextSequences = state.sequences.map(s => ids.includes(s.id) ? { ...s, folderId, name: newNames.get(s.id) ?? s.name } : s);
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, state.poses, nextSequences);
        }
        return { sequences: nextSequences };
      });

      if (auth.currentUser) {
        Promise.all(ids.map(id =>
          fetchWithAuth(`/api/sequences/${id}`, { method: 'PUT', body: JSON.stringify({ folderId, name: newNames.get(id) }) })
        )).catch(err => {
          console.error('Failed to sync sequence multi-move', err);
          set(state => ({
            sequences: state.sequences.map(s => ids.includes(s.id) ? { ...s, folderId: oldFolderIds.get(s.id) ?? null, name: oldNames.get(s.id) ?? s.name } : s)
          }));
          showMutationError();
        });
      }
    },

    multiDeleteSequences: (ids) => {
      ids.forEach(id => {
        const seq = get().sequences.find(s => s.id === id);
        if (seq) trackSequenceEvent('deleted', { sequence_name: seq.name });
      });

      const deletedSeqs = get().sequences.filter(s => ids.includes(s.id));
      set(state => {
        const nextSequences = state.sequences.filter(s => !ids.includes(s.id));
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, state.poses, nextSequences);
        }
        return { sequences: nextSequences };
      });

      if (auth.currentUser) {
        Promise.all(ids.map(id =>
          fetchWithAuth(`/api/sequences/${id}`, { method: 'DELETE' })
        )).catch(err => {
          console.error('Failed to sync sequence multi-delete', err);
          set(state => ({ sequences: [...state.sequences, ...deletedSeqs] }));
          showMutationError();
        });
      }
    },

    savePose: (pose) => {
      const poseWithId = pose.id ? pose : { ...pose, id: generateId() };
      const savedId = poseWithId.id as string;
      const existingPose = get().poses.find(p => p.id === savedId);
      const isExisting = !!existingPose;
      const userId = pose.userId ?? (auth.currentUser ? auth.currentUser.uid : undefined);
      const poseWithUser = { ...poseWithId, ...(userId ? { userId } : {}) } as SavedPose;

      trackPoseEvent('saved', { pose_name: pose.name, is_new: !isExisting });

      set(state => {
        const existingIdx = state.poses.findIndex(p => p.id === savedId);
        let newPoses;
        if (existingIdx >= 0) {
          newPoses = [...state.poses];
          newPoses[existingIdx] = { ...newPoses[existingIdx], ...poseWithUser } as SavedPose;
        } else {
          newPoses = [...state.poses, poseWithUser];
        }
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, newPoses, state.sequences);
        }
        return { poses: newPoses };
      });

      if (auth.currentUser) {
        const bodyData = stripServerOnlyFields(poseWithUser);
        fetchWithAuth(isExisting ? `/api/poses/${savedId}` : '/api/poses', {
          method: isExisting ? 'PUT' : 'POST',
          body: JSON.stringify(isExisting ? bodyData : { ...bodyData, id: savedId })
        }).catch(err => {
          console.error('Failed to sync pose save', err);
          if (isExisting && existingPose) {
            set(state => ({ poses: state.poses.map(p => p.id === savedId ? existingPose : p) }));
          } else {
            set(state => ({ poses: state.poses.filter(p => p.id !== savedId) }));
          }
          showMutationError();
        });
      }

      return savedId;
    },

    renamePose: (id, newName) => {
      const pose = get().poses.find(p => p.id === id);
      if (!pose) return;

      const collision = get().poses.some(p => p.folderId === pose.folderId && p.id !== id && p.name === newName);
      if (collision) {
        throw new Error(NAME_COLLISION);
      }

      trackPoseEvent('renamed', { pose_name: newName, previous_name: pose.name });
      const oldName = pose?.name;

      set(state => {
        const nextPoses = state.poses.map(p => p.id === id ? { ...p, name: newName } : p);
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, nextPoses, state.sequences);
        }
        return { poses: nextPoses };
      });

      if (auth.currentUser) {
        fetchWithAuth(`/api/poses/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: newName })
        }).catch(err => {
          console.error('Failed to sync pose rename', err);
          set(state => ({
            poses: state.poses.map(p => p.id === id ? { ...p, name: oldName! } : p)
          }));
          showMutationError();
        });
      }
    },

    setSavedPoseNotes: (id, notes) => {
      const pose = get().poses.find(p => p.id === id);
      if (!pose) return;
      const oldNotes = pose.pose.notes;

      set(state => {
        const nextPoses = state.poses.map(p => p.id === id ? { ...p, pose: { ...p.pose, notes } } : p);
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, nextPoses, state.sequences);
        }
        return { poses: nextPoses };
      });

      if (auth.currentUser) {
        const updated = get().poses.find(p => p.id === id);
        fetchWithAuth(`/api/poses/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ pose: updated?.pose })
        }).catch(err => {
          console.error('Failed to sync pose note', err);
          set(state => ({
            poses: state.poses.map(p => p.id === id ? { ...p, pose: { ...p.pose, notes: oldNotes } } : p)
          }));
          showMutationError();
        });
      }
    },

    duplicatePose: (id, targetFolderId) => {
      const pose = get().poses.find(p => p.id === id);
      if (!pose) return '';
      const newId = generateId();
      const folderId = targetFolderId !== undefined ? targetFolderId : pose.folderId;
      const uniqueName = getUniquePoseName(get().poses, folderId, `Copy of ${pose.name}`);
      const duplicated: SavedPose = {
        ...pose,
        id: newId,
        name: uniqueName,
        folderId,
        userId: auth.currentUser ? auth.currentUser.uid : undefined,
        pose: { ...pose.pose },
        cameraState: pose.cameraState ? { ...pose.cameraState } : undefined
      };

      trackPoseEvent('saved', { pose_name: duplicated.name, is_new: true, duplicated_from: pose.name });

      set(state => {
        const nextPoses = [...state.poses, duplicated];
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, nextPoses, state.sequences);
        }
        return { poses: nextPoses };
      });

      if (auth.currentUser) {
        fetchWithAuth('/api/poses', {
          method: 'POST',
          body: JSON.stringify(stripServerOnlyFields(duplicated))
        }).catch(err => {
          console.error('Failed to sync pose duplication', err);
          set(state => ({ poses: state.poses.filter(p => p.id !== newId) }));
          showMutationError();
        });
      }

      return newId;
    },

    deletePose: (id) => {
      const pose = get().poses.find(p => p.id === id);
      if (pose) {
        trackPoseEvent('deleted', { pose_name: pose.name });
      }

      set(state => {
        const nextPoses = state.poses.filter(p => p.id !== id);
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, nextPoses, state.sequences);
        }
        return { poses: nextPoses };
      });

      if (auth.currentUser) {
        fetchWithAuth(`/api/poses/${id}`, { method: 'DELETE' })
          .catch(err => {
            console.error('Failed to sync pose delete', err);
            if (pose) set(state => ({ poses: [...state.poses, pose] }));
            showMutationError();
          });
      }
    },

    movePose: (id, folderId) => {
      const pose = get().poses.find(p => p.id === id);
      const folder = get().folders.find(f => f.id === folderId);
      if (pose && folder) {
        trackPoseEvent('moved', { pose_name: pose.name, target_folder_name: folder.name });
      }
      const oldFolderId = pose?.folderId ?? null;
      const newName = pose ? getUniquePoseName(get().poses, folderId, pose.name, id) : undefined;

      set(state => {
        const nextPoses = state.poses.map(p => p.id === id ? { ...p, folderId, ...(newName !== undefined ? { name: newName } : {}) } : p);
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, nextPoses, state.sequences);
        }
        return { poses: nextPoses };
      });

      if (auth.currentUser) {
        fetchWithAuth(`/api/poses/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ folderId, ...(newName !== undefined ? { name: newName } : {}) })
        }).catch(err => {
          console.error('Failed to sync pose move', err);
          set(state => ({
            poses: state.poses.map(p => p.id === id ? { ...p, folderId: oldFolderId, ...(pose ? { name: pose.name } : {}) } : p)
          }));
          showMutationError();
        });
      }
    },

    multiMovePoses: (ids, folderId) => {
      const folder = get().folders.find(f => f.id === folderId);
      if (folder) {
        ids.forEach(id => {
          const pose = get().poses.find(p => p.id === id);
          if (pose) trackPoseEvent('moved', { pose_name: pose.name, target_folder_name: folder.name });
        });
      }

      const oldFolderIds = new Map<string, string | null>(ids.map(id => {
        const pose = get().poses.find(p => p.id === id);
        return [id, pose?.folderId ?? null];
      }));
      const oldNames = new Map<string, string>(ids.map(id => {
        const pose = get().poses.find(p => p.id === id);
        return [id, pose?.name ?? ''];
      }));

      // Disambiguate sequentially against the running set so two moved
      // poses sharing a name don't collide with each other either.
      const newNames = new Map<string, string>();
      const runningNames = get().poses.filter(p => !ids.includes(p.id) && p.folderId === folderId).map(p => p.name);
      ids.forEach(id => {
        const pose = get().poses.find(p => p.id === id);
        if (!pose) return;
        const taken = new Set(runningNames);
        let uniqueName = pose.name;
        if (taken.has(uniqueName)) {
          let n = 2;
          while (taken.has(`${pose.name} (${n})`)) n++;
          uniqueName = `${pose.name} (${n})`;
        }
        newNames.set(id, uniqueName);
        runningNames.push(uniqueName);
      });

      set(state => {
        const nextPoses = state.poses.map(p => ids.includes(p.id) ? { ...p, folderId, name: newNames.get(p.id) ?? p.name } : p);
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, nextPoses, state.sequences);
        }
        return { poses: nextPoses };
      });

      if (auth.currentUser) {
        Promise.all(ids.map(id =>
          fetchWithAuth(`/api/poses/${id}`, { method: 'PUT', body: JSON.stringify({ folderId, name: newNames.get(id) }) })
        )).catch(err => {
          console.error('Failed to sync pose multi-move', err);
          set(state => ({
            poses: state.poses.map(p => ids.includes(p.id) ? { ...p, folderId: oldFolderIds.get(p.id) ?? null, name: oldNames.get(p.id) ?? p.name } : p)
          }));
          showMutationError();
        });
      }
    },

    multiDeletePoses: (ids: string[]) => {
      ids.forEach(id => {
        const pose = get().poses.find(p => p.id === id);
        if (pose) trackPoseEvent('deleted', { pose_name: pose.name });
      });

      const deletedPoses = get().poses.filter(p => ids.includes(p.id));
      set(state => {
        const nextPoses = state.poses.filter(p => !ids.includes(p.id));
        if (!auth.currentUser) {
          persistLocalToDisk(state.folders, nextPoses, state.sequences);
        }
        return { poses: nextPoses };
      });

      if (auth.currentUser) {
        Promise.all(ids.map(id =>
          fetchWithAuth(`/api/poses/${id}`, { method: 'DELETE' })
        )).catch(err => {
          console.error('Failed to sync pose multi-delete', err);
          set(state => ({ poses: [...state.poses, ...deletedPoses] }));
          showMutationError();
        });
      }
    }
  })
);

if (typeof window !== 'undefined') {
  (window as any).storageStore = useStorageStore;
}
